import { lazy, PropsWithChildren, Suspense, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import packageJson from '@/../package.json';
import { DONT_CHECK_BILLING } from '@/constants/common';
import { useAccount } from '@/hooks';
import { useAccountStore, useCommonStore, useProjectsStore } from '@/store';
import { useBrandStrategyStore } from '@/V2/features/brand-strategy/store/brand-strategy-store';
// import { hasBrandStrategyContent } from '@/V2/features/brand-strategy/utils/strategy-utils';
import { TProject } from '@/types/projects.ts';
import { getAccessToken } from '@/lib/utils/auth.ts';
import useAdminGuard from '@/hooks/useAdminGuard';
import useProjects from '@/hooks/useProjects.tsx';

const NotFound = lazy(() => import('@/pages/NotFound'));

const billingPath = '/settings/billing';
const unprotectedPaths = [
  billingPath,
  '/settings/profile',
  '/settings/security',
  '/subscription-success',
  '/admin',
];

const clientDependentPaths = ['brand-settings'];

const ProtectedRoute = ({ children }: PropsWithChildren) => {
  const { data: accountData, isLoading: isAccountDataLoading } = useAccount();
  const setAccountRole = useAccountStore((s) => s.setAccountRole);
  const navigate = useNavigate();
  const { isAdmin, isAdminPath } = useAdminGuard();
  const setAppVersion = useCommonStore((s) => s.setAppVersion);

  const selectedProject = useProjectsStore(
    (s) => s.selectedProject as TProject
  );
  const { projects, isLoading: isClientsLoading } = useProjects();
  const setSelectedProject = useProjectsStore((s) => s.setSelectedProject);
  const setPostsExist = useProjectsStore((s) => s.setPostsExist);
  const setIdeasExist = useProjectsStore((s) => s.setIdeasExist);
  const brandStrategy = useBrandStrategyStore((s) => s.brandStrategy);

  const defaultProject = useAccountStore(
    (state) => state.account?.default_project
  );
  const isItBusiness = useAccountStore((state) => state.isItBusiness);
  const isItMarketer = useAccountStore((state) => state.isItMarketer);
  const { setUserDetails, setPermissions } = useAccountStore();

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    setAppVersion(packageJson?.version || '');
  }, [setAppVersion]);

  useEffect(() => {
    if (!defaultProject || isClientsLoading) return;

    if (accountData && !isAccountDataLoading) {
      setAccountRole(accountData?.data?.role || null);
      setUserDetails({
        id: accountData?.data?.id,
        first_name: accountData?.data?.first_name,
        last_name: accountData?.data?.last_name,
        email: accountData?.data?.email_address,
      });
      setPermissions(accountData?.data?.permissions || []);
    }

    const isUnprotectedPath = unprotectedPaths.indexOf(location.pathname) >= 0;

    const currentPath =
      location.pathname?.split('/')?.filter(Boolean)?.shift() || '';

    let postsExist = false;
    let ideasExist = false;
    if (selectedProject && isItMarketer) {
      // Only check if projects are loaded
      if (projects.length > 0) {
        // Let's check if the stored client belongs to the current account
        let checkedClient = projects.find((c) => c.id === selectedProject.id);

        if (!checkedClient) {
          checkedClient = defaultProject;
          setSelectedProject(defaultProject);
        }
        postsExist = !!checkedClient.has_posts;
        ideasExist = !!checkedClient.has_ideas;
      }
    } else if (isItBusiness) {
      if (selectedProject?.id !== defaultProject?.id) {
        setSelectedProject(defaultProject);
      }

      postsExist = !!defaultProject.has_posts;
      ideasExist = !!defaultProject.has_ideas;
    }

    setPostsExist(postsExist);
    setIdeasExist(ideasExist);

    if (!isItBusiness && !isItMarketer && !DONT_CHECK_BILLING) {
      if (!isUnprotectedPath && location.pathname !== billingPath) {
        navigate(billingPath, { replace: true });
      }
      return;
    }

    const isClientDependentPath =
      clientDependentPaths.indexOf(currentPath) >= 0;

    if (!selectedProject && isClientDependentPath && isItMarketer) {
      navigate('/clients', { replace: true });
      return;
    }

    // Handle brand strategy routing
    // if (
    //   !isUnprotectedPath &&
    //   !isClientDependentPath &&
    //   currentPath !== 'clients'
    // ) {
    // Only make routing decisions if we have a selected project
    // if (selectedProject) {
    //   if (!brandStrategy || !hasBrandStrategyContent(brandStrategy)) {
    //     // No brand strategy exists or it's empty
    //     // Only redirect to setup if we're not already on the setup page
    //     if (location.pathname !== '/brand-wiki-setup') {
    //       navigate('/brand-wiki-setup', { replace: true });
    //     }
    //   } else if (hasBrandStrategyContent(brandStrategy)) {
    //     // If we have a brand strategy with content and we're on the setup page, redirect to main brand strategy page
    //     if (location.pathname === '/brand-wiki-setup') {
    //       navigate('/brand-strategy', { replace: true });
    //     }
    //   }
    //   // If brandStrategy is undefined, it means we're still loading, so don't redirect
    // }
    // }
  }, [
    defaultProject,
    navigate,
    selectedProject,
    projects,
    isClientsLoading,
    isItBusiness,
    isItMarketer,
    setPostsExist,
    setIdeasExist,
    setSelectedProject,
    brandStrategy,
    accountData,
    isAccountDataLoading,
    setPermissions,
    setAccountRole,
    setUserDetails,
  ]);

  return isAdminPath && !isAdmin ? (
    <Suspense fallback={<div>Loading...</div>}>
      <NotFound />
    </Suspense>
  ) : (
    <>{children}</>
  );
};

export default ProtectedRoute;
